import React, { Dispatch, SetStateAction, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { downloadCsv } from '../../../services/downloadBlob';
import errorMessage from '../../../services/errorMessage';
import Button from '../../Button';

export interface ExportCsvButtonProps {
  endpoint: string;
  filename: string;
  params?: Record<string, string | number | undefined>;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  /**
   * Receives the reason when the export fails. Without it a failed export ended
   * the spinner and nothing else, so the admin could not tell it from a download
   * the browser had simply not shown yet.
   */
  onError?: Dispatch<SetStateAction<string | undefined>>;
}

function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * An export asks for a blob, so a failure's JSON error envelope arrives as a
 * Blob too, and errorMessage cannot read the API's sentence out of it. Put the
 * parsed envelope back where errorMessage looks.
 */
export async function withJsonErrorBody(err: unknown): Promise<unknown> {
  const response = (err as { response?: { data?: unknown } })?.response;
  if (!response || !(response.data instanceof Blob)) return err;
  try {
    response.data = JSON.parse(await readBlobText(response.data));
  } catch {
    // Not JSON: errorMessage falls back on the status.
    response.data = undefined;
  }
  return err;
}

/**
 * Domain-specific shim around the canonical <Button> primitive.
 * Wraps services/downloadBlob.downloadCsv with a busy lock and the
 * undefined-param filter. Renders as a secondary outline button so it
 * sits visually below primary actions on the same toolbar.
 */
export default function ExportCsvButton({
  endpoint,
  filename,
  params,
  disabled,
  children,
  className,
  onError,
}: ExportCsvButtonProps) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy || disabled) return;
    setBusy(true);
    onError?.(undefined);
    try {
      const cleaned: Record<string, string | number> = {};
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            cleaned[key] = value as string | number;
          }
        });
      }
      await downloadCsv(endpoint, filename, cleaned);
    } catch (err) {
      if (!onError) throw err;
      errorMessage(onError)((await withJsonErrorBody(err)) as Error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      loading={busy}
      className={className}
      leftIcon={<ArrowDownTrayIcon aria-hidden="true" />}
    >
      {children ?? 'Export CSV'}
    </Button>
  );
}
